'use client';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import { AdvertisementPublicData } from '@cineamo/api/helpers/advertisement-helper';
import { AdImpressionInputDto } from '@cineamo/api/models/advertisement/AdImpressionInputDto.types';
import { AdPlacement } from '@cineamo/api/models/advertisement/AdPlacement.enum';

import { useDeviceType } from '@cineamo/ui/src/helpers/screen-type';

import { storeAdClickAttribution } from './adClickAttribution';
import { useAdvertisement } from './AdvertisementProvider';
import BannerAdvertisement from './BannerAdvertisement';
import LayerAdvertisement from './LayerAdvertisement';
import { AdvertisementRenderLink } from './link';

type AdvertisementRendererProps = {
    type: AdPlacement;
    // Defaults to a plain `<a>` — hosts with locale-aware, typed routing must inject their own.
    renderLink?: AdvertisementRenderLink;
    // Forwarded to LayerAdvertisement; ignored for AdPlacement.TOP_BANNER.
    isInfoBannerVisible?: boolean;
    isMobileUserAgent?: boolean;
    backdropColor?: string;
};

export const AdvertisementRenderer: React.FC<AdvertisementRendererProps> = ({
    type,
    renderLink,
    isInfoBannerVisible,
    isMobileUserAgent,
    backdropColor
}) => {
    const {
        advertisement,
        isVisible: isInitiallyVisible,
        generateUid,
        onImpression,
        onClick,
        onClose
    } = useAdvertisement();
    // Initialize with false and update when data is loaded
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const deviceType = useDeviceType();
    const [uid, setUid] = useState<string | null>(null);
    // Fires the impression once per ad id: guards React 18 StrictMode's double-invoke, and — keyed
    // off the ad id rather than a plain boolean — still fires again when a refetch swaps in a
    // different ad for this placement.
    const firedImpressionForAdId = useRef<AdvertisementPublicData['id'] | null>(null);

    const trackingData = {
        deviceType
    };

    // Update isVisible when isInitiallyVisible changes
    useEffect(() => {
        if (isInitiallyVisible && typeof isInitiallyVisible[type] === 'boolean') {
            setIsVisible(isInitiallyVisible[type]);
        }
    }, [isInitiallyVisible, type]);

    useEffect(() => {
        if (!advertisement || !isVisible || firedImpressionForAdId.current === advertisement.id) {
            return;
        }
        firedImpressionForAdId.current = advertisement.id;
        const sessionId = generateUid();
        setUid(sessionId);
        onImpression(
            {
                ...trackingData,
                uid: sessionId
            } satisfies AdImpressionInputDto,
            type
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [advertisement?.id, isVisible]);

    function onAdvertisementClick() {
        const contentId = advertisement.contentId;
        if (contentId) {
            storeAdClickAttribution(contentId, uid);
        }
        onClick(
            {
                ...trackingData,
                uid
            },
            type
        );
    }

    function onAdvertisementClose() {
        setIsVisible(false);
        onClose(type);
    }

    if (!advertisement || !isVisible) {
        return null;
    }

    if (type === AdPlacement.TOP_BANNER) {
        return (
            <BannerAdvertisement
                advertisement={advertisement}
                onClick={onAdvertisementClick}
                onClose={onAdvertisementClose}
                renderLink={renderLink}
            />
        );
    }

    return (
        <LayerAdvertisement
            advertisement={advertisement}
            onClick={onAdvertisementClick}
            onClose={onAdvertisementClose}
            renderLink={renderLink}
            isInfoBannerVisible={isInfoBannerVisible}
            isMobileUserAgent={isMobileUserAgent}
            backdropColor={backdropColor}
        />
    );
};

export default AdvertisementRenderer;
